/**
 * The `(i)` beside a setting's header, and the one line of explanation behind it.
 *
 * **Tap, not hover.** The product's primary surface is a phone, where there is no hover state to
 * reveal anything — a `title` attribute or a `:hover` bubble is a desktop affordance that reads as
 * a missing feature on the device it ships to. So this is a disclosure: the button toggles, an
 * outside tap or `Escape` closes, and nothing opens on its own.
 *
 * **It is deliberately not one of the three popovers** (`Popover`/`AlertPopover`/`ConfirmPopover`).
 * Those are modal — a backdrop, `aria-modal`, and under D145 a history entry that hardware back
 * consumes. A sentence explaining what *Auto* means is not worth a back tap, and a resident who
 * opened one and then pressed back would expect to leave the screen, not to dismiss a hint. This
 * layer takes no history entry for the same reason it takes no backdrop: it is annotation, not a
 * dialog.
 *
 * **The bubble anchors to the header row, not to this span**, which is the one thing to know
 * before reusing it: `.hint-tip-bubble` is `position: absolute` with `inset-inline` set, so it
 * needs the nearest positioned ancestor to be the row that owns the 16px content inset — the
 * `.has-tip` class, which `global.css` states as one contract with this. Anchoring to the span
 * itself was the first build and it is the wrong shape: the tip sits at the inline-end of a
 * header whose length varies per label, so the room left for the bubble varies with the wording
 * and the longest label pushed it off the frame. Anchored to the row, the bubble is the content
 * column whatever the header says.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from './icons/Icon'

export function HintTip({
  text,
  label,
}: {
  /** The explanation itself — one sentence, already translated. */
  readonly text: string
  /** The button's accessible name, e.g. *"explain: appearance"*. The glyph carries no text. */
  readonly label: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const id = useId()
  const root = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    // `pointerdown` rather than `click`: a tap that begins outside must close the bubble even if
    // it lands on something that swallows the click (a segmented button, the input's own label).
    const away = (event: PointerEvent): void => {
      if (root.current?.contains(event.target as Node) === true) return
      setOpen(false)
    }
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <span className="hint-tip" ref={root}>
      <button
        className="hint-tip-button"
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
      >
        <Icon name="info" size={14} />
      </button>
      {open ? (
        // Tapping the bubble closes it too. It sits over the control it explains, so a finger
        // reaching for that control lands here first — and the outside handler above deliberately
        // ignores anything inside this span, which would otherwise make the bubble the one place
        // on screen a tap does nothing. Not a button: `Escape` and the toggle are the keyboard
        // paths, and a `<button>` wrapping a sentence would be announced as one control.
        <span className="hint-tip-bubble" id={id} role="tooltip" onClick={() => setOpen(false)}>
          {text}
        </span>
      ) : null}
    </span>
  )
}
